<template>
  <!-- side nav-->
  <div id="SideNav" class="side-nav">
    <div class="side-nav-bg"></div>

    <!-- Mobile Menu Content -->
    <div class="mobile-menu-content" @click="handleMenuClick">
      <!-- Close Button -->
      <button aria-label="Menüyü kapat" class="mobile-menu-close" @click.stop="closeMobileMenu">
        <i class="uil-times"></i>
      </button>

      <!-- Company Logo -->
      <div v-if="companyLogo" class="mobile-menu-logo">
        <router-link :to="{ name: 'user.index' }">
          <img :src="companyLogo" alt="">
        </router-link>
      </div>

      <!-- Menu Items -->
      <ul class="mobile-menu-list">
        <!-- Anasayfa -->
        <li>
          <router-link :to="{ name: 'user.index' }" class="menu-item-link">
            <i class="uil-home-alt"></i>
            <span class="menu-item-text">{{ $t('general.homepage') }}</span>
          </router-link>
        </li>
        <li v-for="item in visibleMenuItems" :key="item.type">
          <router-link
              :to="{ name: 'user.' + item.type }"
              class="menu-item-link"
              :class="{ 'router-link-active': isMenuItemActive(item) }">
            <i :class="iconMap[item.type] || 'uil-question-circle'"></i>
            <span class="menu-item-text">
              {{ getMenuItemTitle(item) }}
            </span>
          </router-link>
        </li>
      </ul>

      <!-- Provider Links (Azal) -->
      <ProviderLinks :is-azal="isAzal" :mobile="true"/>

      <!-- Footer Section -->
      <div class="mobile-menu-footer">
        <div class="footer-logo">
        </div>
      </div>
    </div>

    <!-- Desktop Logo -->
    <div class="logo">
      <router-link to="/dashboard">
        <i class="uil-home-alt"></i>
      </router-link>
    </div>

    <!-- Desktop Menu -->
    <ul class="desktop-menu">
      <li v-for="item in visibleMenuItems" :key="item.type">
        <router-link
          :to="{ name: 'user.' + item.type }"
          :class="{ 'router-link-active': isMenuItemActive(item) }"
        >
          <i :class="iconMap[item.type] || 'uil-question-circle'"></i>
          <span class="tooltips">
            {{ getMenuItemTitle(item) }}
          </span>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import axios from "axios";
import SecureLS from "secure-ls";
import generalMixin from "@/core/mixin/general.mixin";
import store from "@/core/services/index";
import { MODULE_NAME as COMPANY_MODULE_NAME, SELF as COMPANY_SELF } from "@/core/services/store/company.module";
import ProviderLinks from "@/view/layouts/user/components/ProviderLinks.vue";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
export default {
  name: "SideNav",
  mixins: [generalMixin],
  components: {
    ProviderLinks,
  },
  data() {
    return {
      moduleList: { menu: [] },
      isTeamManager: false,
      iconMap: {
        'my-assignment': 'uil-book-open',
        'my-statistics': 'uil-chart-line',
        'my-collection': 'uil-notebooks',
        'education-category': 'uil-books',
        'gift': 'uil-gift',
        'wall': 'uil-coffee',
        'agenda': 'uil-calendar-alt',
        'solution_partners_apps': 'uil-heart',
        'my-development': 'uil-chart-growth',
        'my-team': 'uil-users-alt',
      },
      appVersion: '115'
    };
  },
  computed: {
    activeLanguageCode() {
      return String((this.$i18n && this.$i18n.locale) || this.secureLocalStorage("language") || "tr").toLowerCase();
    },
    visibleMenuItems() {
      const sourceMenu = Array.isArray(this.moduleList?.menu)
        ? this.moduleList.menu
        : [];

      // Widget API'sindeki tipleri yerel menü/route tiplerine eşle
      const typeAliases = {
        my_competency: "my-development",
        my_team: "my-team",
      };

      const items = [];
      sourceMenu.forEach((item) => {
        if (!item || !item.type) return;
        const type = typeAliases[item.type] || item.type;
        if (items.some((menuItem) => menuItem.type === type)) return;

        items.push({
          ...item,
          type,
        });
      });

      // Aktif widget'lar; Ekibim yalnızca ekip yöneticilerine gösterilir
      return items.filter((item) => {
        if (Number(item.status) !== 1) return false;
        if (item.type === "my-team" && !this.isTeamManager) return false;
        return true;
      });
    },
    company() {
      return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_SELF];
    },
    companyLogo() {
      if (!this.company) return "";
      const currentSubdomain = window.location.hostname.split('.')[0];
      if (this.company.domain && currentSubdomain === this.company.domain) {
        return this.company.logo_link || "";
      }
      return this.company.parent?.logo_link || this.company.logo_link || "";
    },
    isAzal() {
      return this.company?.alias === "skyacademy";
    }
  },
  mounted() {
    // Önce localStorage'dan kontrol et
    const cachedData = ls.get('dashboardMenuList');
    if (cachedData && cachedData.menu) {
      this.moduleList = cachedData;
      this.moduleList.menu = this.moduleList.menu.sort((a, b) => parseFloat(a.order) - parseFloat(b.order));
    }

    this.fetchWidgets();
  },
  watch: {
    '$i18n.locale'() {
      this.fetchWidgets();
    },
  },
  methods: {
    isMenuItemActive(item) {
      if (!item) return false;
      const routeName = String((this.$route && this.$route.name) || "");
      if (item.type === "my-development") {
        return routeName === "user.my-development" || routeName.startsWith("user.my-development.");
      }
      if (item.type === "my-team") {
        return routeName === "user.my-team" || routeName.startsWith("user.my-team.");
      }
      return false;
    },
    fetchWidgets() {
      if (!(this.authUser && this.authUser.company_id)) {
        return;
      }

      const widgetsRequest = axios.get(
        process.env.VUE_APP_BACKEND_APP_URL + "/api/widgets",
        {
          params: {
            company_id: this.authUser.company_id,
            by_user: true,
          },
        }
      );
      const selfRequest = axios
        .get(process.env.VUE_APP_BACKEND_APP_URL + "/api/user/self")
        .catch(() => null);

      Promise.all([widgetsRequest, selfRequest])
        .then(([response, selfResponse]) => {
          this.moduleList = response.data;
          if (this.moduleList?.menu) {
            this.moduleList.menu = this.moduleList.menu.sort(
              (a, b) => parseFloat(a.order) - parseFloat(b.order)
            );
          }
          this.isTeamManager = !!selfResponse?.data?.is_team_manager;
          ls.set("dashboardMenuList", this.moduleList);
          window.dispatchEvent(new CustomEvent("dashboard-menu-updated"));
        })
        .catch((error) => {
          console.error("Widget loading error:", error);
        });
    },
    getMenuItemTitle(item) {
      if (!item) return "";

      const langTitle = item.lang_title && typeof item.lang_title === "object" ? item.lang_title : null;
      if (langTitle) {
        const code = this.activeLanguageCode;
        const localized =
          langTitle[code] ??
          langTitle[code.toLowerCase()] ??
          langTitle[code.toUpperCase()];

        if (localized !== undefined && localized !== null && String(localized).trim() !== "") {
          return String(localized);
        }
      }

      const title = item.title != null ? String(item.title).trim() : "";
      const typeLang = item.type_lang != null ? String(item.type_lang).trim() : "";

      // Özel menü başlığı (ör. "Gelişimim test 1") API title'dan gelir
      if (title && !title.startsWith("widgets.")) {
        return title;
      }

      // Sistem widget'larında type_lang backend tarafından dile göre gelir
      if (typeLang) return typeLang;

      if (title) {
        const translated = this.$t(title);
        if (translated && String(translated).trim() !== "" && translated !== title) {
          return String(translated);
        }
        return title;
      }

      return "";
    },
    closeMobileMenu() {
      const wrapper = document.getElementById('wrapper');
      if (wrapper && wrapper.classList.contains('mobile-active')) {
        wrapper.classList.remove('mobile-active');
      }
    },
    handleMenuClick(e) {
      // Link veya link içindeki elemente tıklandıysa menüyü kapat
      const clickedLink = e.target.closest('a');
      if (clickedLink) {
        this.closeMobileMenu();
      }
    }
  }
};
</script>

<style scoped>
/* .router-link-exact-active {
  background-color: #F3F3F3;
} */

/* Mobile Menu Styles */
.mobile-menu-content {
  display: none;
}

/* Close Button - Hidden by default */
.mobile-menu-close {
  display: none;
}

@media (max-width: 1220px) {
  .side-nav .side-nav-bg {
    width: 100% !important;
  }

  /* Close Button */
  .mobile-menu-close {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 12px;
    right: 12px;
    width: 36px;
    height: 36px;
    background: transparent;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    z-index: 10;
    color: #6c757d;
    transition: all 0.2s ease;
  }

  .mobile-menu-close:hover {
    background-color: #f8f9fa;
    color: #212529;
  }

  .mobile-menu-close:active {
    background-color: #e9ecef;
    transform: scale(0.95);
  }

  .mobile-menu-close i {
    font-size: 24px;
  }

  .mobile-menu-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    padding: 0;
    position: relative;
    z-index: 2;
  }

  /* Company Logo */
  .mobile-menu-logo {
    padding: 16px 20px;
    padding-top: 52px;
    border-bottom: 1px solid #e9ecef;
  }

  .mobile-menu-logo a {
    display: block;
  }

  .mobile-menu-logo img {
    max-height: 36px;
    width: auto;
    max-width: 160px;
  }

  /* Menu List */
  .mobile-menu-list {
    flex: 1;
    list-style: none;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }

  .mobile-menu-list li {
    margin: 0;
  }

  .menu-item-link {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    color: #495057;
    text-decoration: none;
    transition: all 0.2s ease;
    border-left: 3px solid transparent;
  }

  .menu-item-link:hover {
    background-color: #f8f9fa;
    color: #212529;
  }

  .menu-item-link.router-link-exact-active,
  .menu-item-link.router-link-active {
    background-color: #f8f9fa;
    border-left-color: #666CFF;
    color: #212529;
    font-weight: 500;
  }

  .menu-item-link i {
    font-size: 20px;
    margin-right: 12px;
    width: 24px;
    text-align: center;
    color: #6c757d;
  }

  .menu-item-link.router-link-exact-active i,
  .menu-item-link.router-link-active i {
    color: #666CFF;
  }

  .menu-item-text {
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Footer Section */
  .mobile-menu-footer {
    padding: 20px;
    border-top: 1px solid #e9ecef;
    margin-top: auto;
    text-align: center;
  }

  .footer-logo {
    margin-bottom: 12px;
  }

  .footer-logo img {
    height: 32px;
    width: auto;
    max-width: 120px;
  }

  .footer-version {
    font-size: 12px;
    color: #6c757d;
    margin: 0;
  }

  /* Hide desktop menu and logo on mobile */
  .desktop-menu,
  .logo {
    display: none !important;
  }
}

/* Desktop Styles */
@media (min-width: 1221px) {
  .mobile-menu-content {
    display: none !important;
  }

  .logo {
    display: flex !important;
  }

  .desktop-menu {
    display: block !important;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .side-nav {
    width: 70px !important;
    margin-left: 0 !important;
    transform: none !important;
  }

  .side-nav .side-nav-bg {
    width: 70px !important;
  }

  /* Desktop Menu Active State */
  .desktop-menu a {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px;
    transition: all 0.2s ease;
    border-radius: 8px;
    margin: 4px 8px;
  }

  .desktop-menu a.router-link-exact-active,
  .desktop-menu a.router-link-active {
    background-color: #f8f9fa;
  }

  .desktop-menu a.router-link-exact-active i,
  .desktop-menu a.router-link-active i {
    color: #666CFF;
  }

  .desktop-menu a:hover {
    background-color: #f8f9fa;
  }
}
</style>
